import React from 'react';
import { ArrowDownIcon, ArrowUpIcon } from '@heroicons/react/16/solid';
import { classNames } from '../utils/classNames';
import { FormatOptions, NO_VALUE, ValueFormat, formatChange, formatValue } from './chartFormat';
import type { KpiChange } from './KpiTile';

// Several headline numbers in one card, side by side, with thin lines between them: the top of a
// dashboard (room revenue, occupancy, ADR, RevPAR, room nights). The anatomy of KpiTile: label,
// number, change. The change is a small chip (green for good, red for bad, gray for no direction) with
// the period in its title and for a screen reader, so 5 numbers fit in one row.
// From the sm width the items sit in one row; below it they wrap into 2 columns, and an odd last item
// takes the full row.
// A null value shows '–'. The strip knows nothing about the data behind the numbers.

export type KpiStripItem = {
	id: string,
	// Sentence case, no colon: "Room revenue".
	label: string,
	value: number | null,
	format: ValueFormat,
	currency?: string,
	decimals?: number,
	change?: KpiChange,
};

type Props = {
	items: KpiStripItem[],
	// The name of the strip for a screen reader ("Santikos Collection, 2026").
	ariaLabel: string,
	state?: 'default' | 'loading',
};

// Static class names, so that Tailwind keeps them.
const COLUMNS: Record<number, string> = {
	2: 'sm:grid-cols-2', 3: 'sm:grid-cols-3', 4: 'sm:grid-cols-4', 5: 'sm:grid-cols-5', 6: 'sm:grid-cols-6',
};

const ChangeChip: React.FunctionComponent<{ change: KpiChange, options: FormatOptions }> = ({ change, options }) => {
	const up = change.value > 0;
	const down = change.value < 0;
	const upIsGood = change.upIsGood === undefined ? true : change.upIsGood;
	const good = upIsGood === null || (!up && !down) ? null : (up === upIsGood);
	const tone = good === null ? 'bg-gray-50 text-gray-600 ring-gray-500/10' : (good ? 'bg-green-50 text-green-700 ring-green-700/10' : 'bg-red-50 text-red-700 ring-red-700/10');
	const Icon = up ? ArrowUpIcon : ArrowDownIcon;
	return <span title={change.label} className={classNames('inline-flex items-center gap-x-0.5 rounded-md px-1.5 py-0.5 text-xs font-semibold ring-1 ring-inset', tone)}>
		{(up || down) && <Icon aria-hidden="true" className="h-3 w-3 shrink-0" />}
		<span className="sr-only">{up ? 'Up' : (down ? 'Down' : 'No change,')}</span>
		{(up ? '+' : (down ? '−' : '')) + formatChange(change.value, change.format, options)}
		<span className="sr-only"> {change.label}</span>
	</span>;
};

const KpiStrip: React.FunctionComponent<Props> = ({ items, ariaLabel, state = 'default' }) => {
	const odd = items.length % 2 === 1;
	return <section aria-label={ariaLabel} aria-busy={state === 'loading'}
		className={classNames('grid grid-cols-2 overflow-hidden rounded-lg bg-white shadow', COLUMNS[items.length] ?? 'sm:grid-cols-5')}>
		{items.map((item, i) => {
			const options: FormatOptions = { currency: item.currency, decimals: item.decimals ?? 0 };
			const last = i === items.length - 1;
			return <dl key={item.id} className={classNames(
				'min-w-0 px-4 py-3 sm:px-5 sm:py-4',
				// Lines between the items: left on the 2nd column (mobile) and on every item after the first (sm and up).
				i % 2 === 1 ? 'border-l border-gray-200' : '',
				i >= 2 ? 'border-t border-gray-200 sm:border-t-0' : '',
				i > 0 ? 'sm:border-l sm:border-gray-200' : '',
				odd && last ? 'col-span-2 sm:col-span-1' : '',
			)}>
				<dt className="truncate text-sm font-semibold text-gray-500">{item.label}</dt>
				{state === 'loading'
					? <dd className="animate-pulse">
						<span className="sr-only">Loading</span>
						<div className="mt-2 h-6 w-24 rounded bg-gray-200" />
						<div className="mt-2 h-4 w-14 rounded bg-gray-100" />
					</dd>
					: <dd>
						<p className="mt-1 text-xl font-bold tracking-tight text-gray-900 sm:text-2xl">
							{item.value === null ? NO_VALUE : formatValue(item.value, item.format, options)}
						</p>
						{item.change !== undefined && <div className="mt-1.5"><ChangeChip change={item.change} options={{}} /></div>}
					</dd>}
			</dl>;
		})}
	</section>;
};

export default KpiStrip;
